import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ChangeEvent, Dispatch, RefObject, SetStateAction } from 'react'
import { TEMPLATE_CATEGORIES } from '@constants/Notes/templateCategories'
import type { NotificationToastData } from '@components/common/NotificationToast/NotificationToast.component'
import type {
  TemplateCategory,
  TemplateSubcategory,
} from '@dataTypes/templateCategories'
import {
  createNoteTemplate as createNoteTemplateApi,
  deleteNoteTemplate as deleteNoteTemplateApi,
  getNoteTemplates as fetchNoteTemplatesApi,
  updateNoteTemplate as updateNoteTemplateApi,
} from '@services/api/note.api'
import {
  createFailedToast,
  createSuccessToast,
} from '@utils/notificationToast.util'
import { findMatchingCustomTemplate } from './noteEditor.helpers'
import type {
  AnchoredPopoverPosition,
  CustomTemplate,
  TemplateOption,
} from './noteEditor.types'

type UseNoteEditorTemplatesParams = {
  patientName: string
  setNoteContent: Dispatch<SetStateAction<string>>
  showToast: (notification: NotificationToastData) => void
}

export type NoteEditorTemplates = {
  customTemplates: CustomTemplate[]
  selectedTemplate: string
  setSelectedTemplate: Dispatch<SetStateAction<string>>
  showTemplateDropdown: boolean
  setShowTemplateDropdown: Dispatch<SetStateAction<boolean>>
  templateSearchQuery: string
  setTemplateSearchQuery: Dispatch<SetStateAction<string>>
  templateDropdownPosition: AnchoredPopoverPosition | null
  setTemplateDropdownPosition: Dispatch<
    SetStateAction<AnchoredPopoverPosition | null>
  >
  expandedCategories: Set<string>
  setExpandedCategories: Dispatch<SetStateAction<Set<string>>>
  showCustomTemplateModal: boolean
  setShowCustomTemplateModal: Dispatch<SetStateAction<boolean>>
  editingTemplateId: string | null
  setEditingTemplateId: Dispatch<SetStateAction<string | null>>
  setTemplateButtonLabel: Dispatch<SetStateAction<string>>
  templateDropdownRef: RefObject<HTMLDivElement>
  filteredTemplates: TemplateOption[]
  getTemplateContent: (template: string) => string
  getTemplateDisplayName: (value: string) => string
  handleTemplateClick: () => void
  handleTemplateSearch: (event: ChangeEvent<HTMLInputElement>) => void
  handleCustomTemplateSubmit: (payload: {
    name: string
    content: string
    templateId?: string
    existingTemplateId?: string
  }) => Promise<void>
  handleCustomTemplateDelete: (templateId: string) => Promise<void>
}

export const useNoteEditorTemplates = ({
  patientName,
  setNoteContent,
  showToast,
}: UseNoteEditorTemplatesParams): NoteEditorTemplates => {
  const showToastRef = useRef(showToast)
  showToastRef.current = showToast
  const [selectedTemplate, setSelectedTemplate] = useState<string>('')
  const [showTemplateDropdown, setShowTemplateDropdown] =
    useState<boolean>(false)
  const [templateSearchQuery, setTemplateSearchQuery] = useState<string>('')
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(),
  )
  const [templateDropdownPosition, setTemplateDropdownPosition] =
    useState<AnchoredPopoverPosition | null>(null)
  const [customTemplates, setCustomTemplates] = useState<CustomTemplate[]>([])
  const [, setCustomTemplatesLoading] = useState(false)
  const [showCustomTemplateModal, setShowCustomTemplateModal] = useState(false)
  const [editingTemplateId, setEditingTemplateId] = useState<string | null>(
    null,
  )
  const [templateButtonLabel, setTemplateButtonLabel] = useState('Template')
  const templateDropdownRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const updateLabel = (): void => {
      const labelElement = document.querySelector(
        '.rich-text-editor-container .template-toolbar-button .template-toolbar-label',
      )
      if (labelElement) {
        labelElement.textContent = templateButtonLabel
      }
    }
    updateLabel()
    const timeoutId = window.setTimeout(updateLabel, 150)
    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [templateButtonLabel])

  useEffect(() => {
    let cancelled = false
    setCustomTemplatesLoading(true)
    fetchNoteTemplatesApi()
      .then((res) => {
        if (cancelled || res.statusCode !== 200 || !res.data?.templates) return
        const list: CustomTemplate[] = (
          res.data.templates as Array<{
            template_id: string
            name: string
            content: string
            category?: string | null
          }>
        ).map((t) => ({
          id: t.template_id,
          name: t.name,
          content: t.content,
          category: t.category ?? undefined,
        }))
        setCustomTemplates(list)
      })
      .catch(() => {
        if (!cancelled) {
          showToastRef.current(
            createFailedToast('Error', 'Failed to load custom templates.'),
          )
        }
      })
      .finally(() => {
        if (!cancelled) setCustomTemplatesLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  const templateCategoriesWithCustom = useMemo<TemplateCategory[]>(() => {
    const customTemplatesByCategory = customTemplates.reduce(
      (acc, template) => {
        const category = template.category || 'Custom'
        if (!acc[category]) {
          acc[category] = []
        }
        acc[category].push(template)
        return acc
      },
      {} as Record<string, CustomTemplate[]>,
    )
    return TEMPLATE_CATEGORIES.map((category) => {
      const categoryCustomTemplates =
        customTemplatesByCategory[category.value] || []
      const customSubcategories = categoryCustomTemplates.map((template) => ({
        text: template.name,
        value: template.id,
        icon: 'custom-template-edit',
      }))
      if (category.value === 'Custom') {
        const addCustomEntry = category.subcategories.find(
          (sub) => sub.value === 'Custom_Add',
        )
        const remainingEntries = category.subcategories.filter(
          (sub) => sub.value !== 'Custom_Add',
        )
        return {
          ...category,
          subcategories: [
            ...customSubcategories,
            ...remainingEntries,
            ...(addCustomEntry ? [addCustomEntry] : []),
          ],
        }
      }
      return {
        ...category,
        subcategories: customSubcategories,
      }
    })
  }, [customTemplates])

  const templateOptions = useMemo<TemplateOption[]>(
    () => [
      { text: '', value: '', isCategory: false },
      ...templateCategoriesWithCustom.flatMap((category) => [
        { text: category.text, value: category.value, isCategory: true },
        ...category.subcategories.map((sub) => ({
          text: `  ${sub.text}`,
          value: sub.value,
          isCategory: false,
          icon: sub.icon,
        })),
      ]),
    ],
    [templateCategoriesWithCustom],
  )

  const getTemplateDisplayName = useCallback(
    (value: string): string => {
      if (!value) return 'Template'
      for (const category of templateCategoriesWithCustom) {
        if (category.value === value) {
          return category.text
        }
        const matchingSub = category.subcategories.find(
          (subCategory) => subCategory.value === value,
        )
        if (matchingSub) {
          const subLabel = matchingSub.text.trim()
          return `${category.text} - ${subLabel}`
        }
      }
      const customTemplate = customTemplates.find(
        (customTemplateItem) => customTemplateItem.id === value,
      )
      if (customTemplate) {
        const categoryName = customTemplate.category || 'Custom'
        return `${categoryName} - ${customTemplate.name}`
      }
      return 'Template'
    },
    [templateCategoriesWithCustom, customTemplates],
  )

  useEffect(() => {
    setTemplateButtonLabel(getTemplateDisplayName(selectedTemplate))
  }, [selectedTemplate, getTemplateDisplayName])

  const substituteTemplatePlaceholders = useCallback(
    (content: string): string => {
      const currentDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
      return content
        .replace(/\{\{patientName\}\}/g, patientName)
        .replace(/\{\{currentDate\}\}/g, currentDate)
    },
    [patientName],
  )

  const getTemplateContent = (template: string): string => {
    const matched = findMatchingCustomTemplate(template, customTemplates)
    if (!matched) return ''
    return substituteTemplatePlaceholders(matched.content)
  }

  const handleTemplateClick = useCallback(() => {
    setShowTemplateDropdown((prev) => {
      const next = !prev
      if (next) {
        setTemplateSearchQuery('')
        const templateButton = document.querySelector(
          '.rich-text-editor-container .custom-toolbar-button.template',
        ) as HTMLElement | null
        if (templateButton) {
          const rect = templateButton.getBoundingClientRect()
          setTemplateDropdownPosition({
            top: rect.bottom + 4,
            left: rect.left,
          })
        } else {
          setTemplateDropdownPosition(null)
        }
      } else {
        setTemplateSearchQuery('')
        setTemplateDropdownPosition(null)
      }
      return next
    })
  }, [])

  const handleTemplateSearch = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setTemplateSearchQuery(event.target.value)
    },
    [],
  )

  const handleCustomTemplateSubmit = useCallback(
    async (payload: {
      name: string
      content: string
      templateId?: string
      existingTemplateId?: string
    }) => {
      const { name, content, templateId, existingTemplateId } = payload
      let category = 'Custom'
      if (templateId) {
        const isValidCategory = TEMPLATE_CATEGORIES.some(
          (cat) => cat.value === templateId,
        )
        if (isValidCategory) {
          category = templateId
        }
      }
      try {
        if (existingTemplateId) {
          const res = await updateNoteTemplateApi(existingTemplateId, {
            name,
            content,
            category: category || undefined,
          })
          if (res.statusCode !== 200 || !res.data?.template) {
            showToast(
              createFailedToast(
                'Error',
                res.message || 'Failed to update template.',
              ),
            )
            return
          }
          const t = res.data.template
          setCustomTemplates((prev) =>
            prev.map((item) =>
              item.id === existingTemplateId
                ? {
                    id: t.template_id,
                    name: t.name,
                    content: t.content,
                    category: t.category ?? undefined,
                  }
                : item,
            ),
          )
          setSelectedTemplate(t.template_id)
          setNoteContent(substituteTemplatePlaceholders(t.content))
          setTemplateButtonLabel(getTemplateDisplayName(t.template_id))
          setExpandedCategories(new Set([t.category ?? 'Custom']))
          showToast(createSuccessToast('Success', 'Template updated.'))
        } else {
          const res = await createNoteTemplateApi({ name, content, category })
          if (res.statusCode !== 200 && res.statusCode !== 201) {
            showToast(
              createFailedToast(
                'Error',
                res.message || 'Failed to create template.',
              ),
            )
            return
          }
          const t = res.data?.template
          if (!t) {
            showToast(createFailedToast('Error', 'Failed to create template.'))
            return
          }
          const newTemplate: CustomTemplate = {
            id: t.template_id,
            name: t.name,
            content: t.content,
            category: t.category ?? undefined,
          }
          setCustomTemplates((prev) => [...prev, newTemplate])
          setSelectedTemplate(t.template_id)
          setNoteContent(substituteTemplatePlaceholders(t.content))
          setTemplateButtonLabel(getTemplateDisplayName(t.template_id))
          setExpandedCategories(new Set([t.category ?? 'Custom']))
          showToast(createSuccessToast('Success', 'Template created.'))
        }
        setShowCustomTemplateModal(false)
        setEditingTemplateId(null)
      } catch {
        showToast(createFailedToast('Error', 'Failed to save template.'))
      }
    },
    [
      getTemplateDisplayName,
      setNoteContent,
      showToast,
      substituteTemplatePlaceholders,
    ],
  )

  const handleCustomTemplateDelete = useCallback(
    async (templateId: string) => {
      try {
        const res = await deleteNoteTemplateApi(templateId)
        if (res.statusCode !== 200) {
          showToast(
            createFailedToast(
              'Error',
              res.message || 'Failed to delete template.',
            ),
          )
          return
        }
        setCustomTemplates((prev) => prev.filter((t) => t.id !== templateId))
        if (selectedTemplate === templateId) {
          setSelectedTemplate('')
          setNoteContent('')
          setTemplateButtonLabel('')
        }
        setShowCustomTemplateModal(false)
        setEditingTemplateId(null)
        showToast(createSuccessToast('Success', 'Template deleted.'))
      } catch {
        showToast(createFailedToast('Error', 'Failed to delete template.'))
      }
    },
    [selectedTemplate, setNoteContent, showToast],
  )

  const filteredTemplates = useMemo((): TemplateOption[] => {
    let options = templateOptions
    if (templateSearchQuery) {
      options = options.filter((option) =>
        option.text.toLowerCase().includes(templateSearchQuery.toLowerCase()),
      )
    } else {
      options = templateOptions.filter((option) => {
        if (option.value === '') return true
        if (option.isCategory) return true
        const parentCategory = templateCategoriesWithCustom.find(
          (cat: TemplateCategory) =>
            cat.subcategories.some(
              (sub: TemplateSubcategory) => sub.value === option.value,
            ),
        )
        return parentCategory
          ? expandedCategories.has(parentCategory.value)
          : false
      })
    }
    return options
  }, [
    templateSearchQuery,
    expandedCategories,
    templateOptions,
    templateCategoriesWithCustom,
  ])

  return {
    customTemplates,
    selectedTemplate,
    setSelectedTemplate,
    showTemplateDropdown,
    setShowTemplateDropdown,
    templateSearchQuery,
    setTemplateSearchQuery,
    templateDropdownPosition,
    setTemplateDropdownPosition,
    expandedCategories,
    setExpandedCategories,
    showCustomTemplateModal,
    setShowCustomTemplateModal,
    editingTemplateId,
    setEditingTemplateId,
    setTemplateButtonLabel,
    templateDropdownRef,
    filteredTemplates,
    getTemplateContent,
    getTemplateDisplayName,
    handleTemplateClick,
    handleTemplateSearch,
    handleCustomTemplateSubmit,
    handleCustomTemplateDelete,
  }
}
